import { useEffect, useRef } from 'react';
import { useAppStore } from '@/stores/app-store';
import { fetchProfile } from '@/lib/auth-api';
import { trackException } from '@/lib/app-insights';

export function useBootstrapAuth(): void {
  const bootstrapped = useRef(false);
  const setCurrentUser = useAppStore((s) => s.setCurrentUser);
  const isAuthenticated = useAppStore((s) => s.isAuthenticated);

  // Intentional deviation from Rule 1: profile bootstrap is a one-shot mount
  // side-effect, not cached server state. It needs no polling, invalidation, or
  // cache key — useQuery would be overkill. currentUser in Zustand is auth state
  // (not duplicated server cache); this is the only place it is hydrated.
  useEffect(() => {
    if (bootstrapped.current) return;
    // Arm the guard on this first run regardless of outcome — otherwise an in-session
    // sign-in (which flips isAuthenticated false -> true after already populating
    // currentUser itself) re-triggers this effect and fires a redundant fetchProfile()
    // that can resolve much later and overwrite newer state (e.g. a just-created
    // wholesaler) with the stale pre-sign-in snapshot.
    bootstrapped.current = true;
    const token = sessionStorage.getItem('access_token');
    if (!token || !isAuthenticated) return;

    fetchProfile()
      .then((profile) => {
        setCurrentUser(profile);
      })
      .catch((err: unknown) => {
        // 401 is handled by the Axios interceptor; any other error is unexpected
        trackException(err);
      });
  }, [isAuthenticated, setCurrentUser]);
}
